import { Button } from '../primitives/button.js'
import { graphHooks } from '@tunasong/graph-lib/react'
import { Controls } from '@tunasong/icons'
import { SystemTags, canWriteEntity, hasSystemTag } from '@tunasong/models'
import type { BaseContentContainerProps } from '@tunasong/plugin-lib'
import { useCurrentUser } from '@tunasong/redux'
import type { Entity, Image, ImageElement, Persisted } from '@tunasong/schemas'
import type { FC } from 'react'
import React, { useRef, useState } from 'react'
import CloseButton from '../dialog/close-button.js'
import Dialog from '../dialog/dialog.js'
import EntityTitle from '../entity/entity-title.js'
import BacklinksButton from '../graph/backlinks-button.js'
import { useMergeRefs } from '../hooks/merge-refs.js'
import type { ScrollState } from '../hooks/scroll-state.js'
import { useScrollState } from '../hooks/scroll-state.js'
import { BackgroundImage } from '../image/background-image.js'
import { HeaderImage } from '../image/header-image.js'
import ContentContainerSettings from './content-container-settings.js'
import { TemplateHeader } from './template-header.js'
import TrashHeader from './trash-header.js'
import styles from './content-container.module.css'

export interface ContentContainerProps extends BaseContentContainerProps {
  className?: string
  slots?: {
    EntityControls?: FC<{ entity?: Persisted<Entity>; onActive?: (active: boolean) => void }>
  }
  /** Show a close button, and call onClose when it's clicked */
  onClose?(): void
  /** Called when there is a change in scroll state */
  onScrollChange?(scrollState: ScrollState): void
}

export const ContentContainer: FC<ContentContainerProps> = props => {
  const {
    entity,
    large = false,
    showBacklinks = true,
    showTitle = true,
    onClose,
    slots: { EntityControls } = {},
    children,
    contentBoxProps,
    onScrollChange,
    ref: _ref,
    className,
    style,
  } = props

  const { scrollRef } = useScrollState({ onScrollChange })

  const { child: backgroundImage } = graphHooks.useEntityChild<Persisted<ImageElement>>({
    parentId: entity?.id,
    filter: hasSystemTag(SystemTags.BackgroundImage),
  })

  const { child: headerImage } = graphHooks.useEntityChild<Persisted<Image>>({
    parentId: entity?.id,
    filter: hasSystemTag(SystemTags.HeaderImage),
  })

  const { userId } = useCurrentUser()
  const canEdit = canWriteEntity(userId, entity)

  const contentRef = useRef<HTMLDivElement | null>(null)

  const boxRef = useMergeRefs(scrollRef, contentRef) as React.Ref<HTMLDivElement>

  // Filter by tags
  const [showContentSettings, setShowContentSettings] = useState(false)

  const rootClasses = [styles.root, className].filter(Boolean).join(' ')

  return (
    // This is the scroll container for the contentEl
    <div
      className={rootClasses}
      style={{
        maxWidth: large ? 'fit-content' : 1024,
        ...style,
      }}
    >
      <TrashHeader style={{ flexDirection: 'row' }} entity={entity} />
      <TemplateHeader entity={entity} />
      {headerImage && <HeaderImage image={headerImage} />}
      {backgroundImage ? <BackgroundImage image={backgroundImage} /> : null}
      {entity ? (
        <>
          {/* Header controls & info */}
          <div className={styles.headerControls}>
            <div className={`${styles.headerLeft} ${styles.quiet}`}>
              <Button
                startIcon={<Controls />}
                aria-label="View settings"
                aria-haspopup="dialog"
                onClick={() => setShowContentSettings(true)}
              />
            </div>
            {showTitle ? <EntityTitle entity={entity} disabled={!canEdit} /> : <div />}

            {/* Right */}
            <div className={`${styles.headerRight} ${styles.quiet}`}>
              {showBacklinks ? <BacklinksButton id={entity.id} /> : null}
              {onClose ? <CloseButton onClose={onClose} /> : null}
            </div>
          </div>
        </>
      ) : null}
      <div
        className={[styles.contentBox, contentBoxProps?.className].filter(Boolean).join(' ')}
        ref={boxRef}
        style={contentBoxProps?.style}
      >
        {entity ? (
          <Dialog
            title="View settings"
            maxWidth="xs"
            fullWidth
            open={showContentSettings}
            onClose={() => setShowContentSettings(false)}
          >
            <ContentContainerSettings entity={entity} EntityControls={EntityControls} />
          </Dialog>
        ) : null}
        {children}
      </div>
    </div>
  )
}

export default ContentContainer
